import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, Shirt } from "lucide-react";
import { toast } from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";

export default function LoginPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const expiredToastShownRef = useRef(false);
  const isRegisterMode = location.pathname === "/register";

  const { login } = useAuthStore();
  const navigate = useNavigate();
  const nextPath = useMemo(() => {
    const rawNext = (searchParams.get("next") ?? "").trim();
    if (!rawNext.startsWith("/") || rawNext.startsWith("//")) {
      return "/dashboard";
    }
    return rawNext;
  }, [searchParams]);
  const authSwitchSearch = useMemo(() => {
    const rawNext = (searchParams.get("next") ?? "").trim();
    if (!rawNext.startsWith("/") || rawNext.startsWith("//")) {
      return "";
    }
    return `?next=${encodeURIComponent(rawNext)}`;
  }, [searchParams]);

  useEffect(() => {
    if (
      searchParams.get("reason") !== "expired" ||
      expiredToastShownRef.current
    ) {
      return;
    }
    expiredToastShownRef.current = true;
    toast.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
  }, [searchParams]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isRegisterMode && password !== confirmPassword) {
      toast.error("Mật khẩu xác nhận không khớp");
      return;
    }

    setIsLoading(true);

    try {
      const payload = isRegisterMode
        ? await api.register({ username, email, password })
        : await api.login(username, password);

      login(
        payload.user,
        payload.tokens.accessToken,
        payload.tokens.refreshToken,
      );
      toast.success(
        isRegisterMode ? "Đăng ký thành công" : "Đăng nhập thành công",
      );
      navigate(nextPath, { replace: true });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : isRegisterMode
            ? "Đăng ký thất bại"
            : "Đăng nhập thất bại",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-6xl items-center px-4 py-10">
      <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,1.1fr)_420px]">
        <section className="flex flex-col justify-center rounded-[2rem] border border-border/80 bg-card/70 p-8 backdrop-blur-xl lg:p-10">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/12 text-primary">
            <Shirt className="h-7 w-7" />
          </div>
          <div className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
              ClothesWeb Admin
            </p>
            <h1 className="max-w-xl text-3xl font-semibold tracking-tight lg:text-4xl">
              Một workspace gọn cho catalog, pricing và runtime operations.
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground lg:text-base">
              Public clothes library vẫn mở để duyệt asset. Chỉ tài khoản admin
              mới được cập nhật storefront, giá bán và dữ liệu clothing shop.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-border/80 bg-muted/20 p-4">
              <p className="text-sm font-medium">Read-first catalog</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Duyệt mesh part, texture variant và preview ngay cả khi chưa đăng nhập.
              </p>
            </div>
            <div className="rounded-2xl border border-border/80 bg-muted/20 p-4">
              <p className="text-sm font-medium">Admin-only pricing</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Role admin kiểm soát chỉnh giá, storefront mode và clothing shop sync.
              </p>
            </div>
            <div className="rounded-2xl border border-border/80 bg-muted/20 p-4">
              <p className="text-sm font-medium">Bootstrapped access</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Tài khoản đăng ký đầu tiên sẽ được cấp quyền admin để khởi tạo hệ thống.
              </p>
            </div>
          </div>
        </section>

        <Card className="border-border/80 bg-card/90 backdrop-blur-xl">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-semibold">
              {isRegisterMode ? "Create account" : "Sign in"}
            </CardTitle>
            <CardDescription>
              {isRegisterMode
                ? "Đăng ký tài khoản mới. User đầu tiên sẽ tự nhận quyền admin."
                : "Dùng tài khoản hiện có để vào khu vực quản trị."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                />
              </div>

              {isRegisterMode ? (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {isRegisterMode ? (
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword((current) => !current)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isRegisterMode ? "Create account" : "Sign in"}
              </Button>
            </form>

            <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/80 bg-muted/20 p-4 text-sm">
              <div>
                <p className="font-medium">
                  {isRegisterMode ? "Đã có tài khoản?" : "Chưa có tài khoản?"}
                </p>
                <p className="text-muted-foreground">
                  {isRegisterMode
                    ? "Quay lại đăng nhập để vào workspace."
                    : "Tạo tài khoản mới để bắt đầu quản trị."}
                </p>
              </div>
              <Button asChild variant="outline">
                <Link to={`${isRegisterMode ? "/login" : "/register"}${authSwitchSearch}`}>
                  {isRegisterMode ? "Sign in" : "Register"}
                </Link>
              </Button>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/80 bg-muted/20 p-4 text-sm">
              <div>
                <p className="font-medium">Chỉ cần browse catalog?</p>
                <p className="text-muted-foreground">
                  Bạn có thể vào public clothes library mà không cần đăng nhập.
                </p>
              </div>
              <Button asChild variant="outline">
                <Link to="/assets/clothes">Open catalog</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
